import React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import { useAuthState } from "../app/AuthStateContext";
import { getOrCreateVisitorId } from "../../universal/track";
import {
  appendAttributionQueryParams,
  getStoredAttribution,
} from "../../universal/campaignAttribution";
import { clearExplicitLogout } from "../../universal/explicitLogoutGuard";
import { useTemplateTrack } from "../templates/TemplateTrackingProvider";

/**
 * Steam OpenID sign-in button.
 *
 * Steam OpenID is a top-level redirect, not a popup, so this is implemented
 * as an `<a>`-style button that navigates the browser to `/auth/steam`. We
 * deliberately render the assurance microcopy directly under the button to
 * defuse the legitimate scam-skepticism a CS2 audience has about anything
 * asking them to "sign in with Steam" — most scams ask for trade tokens or
 * API keys, neither of which OpenID can request.
 *
 * Visual styling lives in publicSiteStyles.js (`steamButton`, `assuranceText`).
 */
const STEAM_LOGO_PATH =
  "M12.026 0C5.535 0 .228 5.034 0 11.41l6.467 2.659a3.66 3.66 0 0 1 2.033-.628c.062 0 .124.001.185.005l2.873-4.137v-.06c0-2.498 2.044-4.531 4.555-4.531 2.51 0 4.554 2.033 4.554 4.531 0 2.498-2.044 4.531-4.555 4.531a4.58 4.58 0 0 1-.131-.002l-4.094 2.901c.001.052.004.104.004.156 0 1.999-1.638 3.625-3.65 3.625-1.768 0-3.244-1.243-3.582-2.918L.218 15.674C1.704 20.452 6.45 24 12.026 24c6.633 0 12.013-5.349 12.013-11.94 0-6.59-5.38-12.06-12.013-12.06zM7.479 18.299l-1.482-.61c.262.546.715.998 1.32 1.25 1.31.546 2.821-.067 3.371-1.367a2.55 2.55 0 0 0 .005-1.957c-.27-.642-.778-1.146-1.426-1.418-.643-.27-1.328-.26-1.937-.039l1.531.632c.97.405 1.428 1.516 1.022 2.482-.405.965-1.522 1.42-2.488 1.014l.084.013zm10.572-9.094c0-1.66-1.359-3.012-3.029-3.012-1.671 0-3.029 1.351-3.029 3.012 0 1.661 1.358 3.012 3.029 3.012 1.67 0 3.029-1.351 3.029-3.012zm-5.301-.005a2.275 2.275 0 0 1 2.281-2.262 2.275 2.275 0 0 1 2.28 2.262 2.275 2.275 0 0 1-2.28 2.262 2.275 2.275 0 0 1-2.281-2.262z";

function SteamLogo({ size = 22, color = "currentColor" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-hidden="true"
      focusable="false"
      style={{ display: "block", flexShrink: 0 }}
    >
      <path d={STEAM_LOGO_PATH} fill={color} />
    </svg>
  );
}

SteamLogo.propTypes = {
  size: PropTypes.number,
  color: PropTypes.string,
};

export default function SteamSignInButton({
  sx = {},
  assuranceSx = {},
  showAssurance = true,
  label = "Sign in with Steam",
  logoSize = 22,
  fullWidth = false,
  surface = "hero",
}) {
  const { steamSignInUrl } = useAuthState();
  const track = useTemplateTrack();
  const baseUrl = steamSignInUrl || "/auth/steam";

  // The href stays as the bare baseUrl so middle-click / ctrl-click / right-click
  // "open in new tab" all behave normally (they hit /auth/steam directly; the
  // server middleware defaults missing surface/vid to "hero" / "unknown"). Only
  // a plain primary-button click runs the augmenting onClick — and we deliberately
  // generate the visitor ID at click time, NOT at render, because cookieStorage
  // is a no-op on the SSR pass and computing it during render would cause an
  // SSR/CSR href mismatch and a hydration warning.
  function handleClick(e) {
    // Let the browser handle modifier-keyed clicks via the bare href.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    clearExplicitLogout();
    track("sso_steam_clicked", { provider: "steam" });
    const vid = getOrCreateVisitorId();
    const separator = baseUrl.includes("?") ? "&" : "?";
    let targetUrl = `${baseUrl}${separator}surface=${encodeURIComponent(surface)}&vid=${encodeURIComponent(vid)}`;

    // Append last-touch campaign attribution so Edge can store it at signup time.
    const acq = getStoredAttribution();
    if (acq) {
      targetUrl = appendAttributionQueryParams(targetUrl, acq);
    }

    window.location.href = targetUrl;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: fullWidth ? "100%" : "auto",
        maxWidth: fullWidth ? "100%" : "none",
        minWidth: 0,
        boxSizing: "border-box",
      }}
    >
      <Button
        href={baseUrl}
        onClick={handleClick}
        variant="contained"
        disableElevation
        startIcon={<SteamLogo size={logoSize} color="currentColor" />}
        sx={{
          gap: { xs: 0.75, md: 1 },
          ...sx,
          width: fullWidth ? "100%" : "auto",
          maxWidth: "100%",
          boxSizing: "border-box",
        }}
      >
        {label}
      </Button>
      {showAssurance ? (
        <Typography component="p" sx={assuranceSx}>
          Read-only sign-in via Steam. We never request inventory, trade, or market permissions.
          New accounts need public CS2 hours.
        </Typography>
      ) : null}
    </Box>
  );
}

SteamSignInButton.propTypes = {
  sx: PropTypes.object,
  assuranceSx: PropTypes.object,
  showAssurance: PropTypes.bool,
  label: PropTypes.string,
  logoSize: PropTypes.number,
  fullWidth: PropTypes.bool,
  surface: PropTypes.string,
};
